import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTheme } from '@/context/ThemeContext'
import { Sidebar } from '@/components/ui/sidebar'
import BottomNav from '@/components/ui/bottom-nav'
import {
  useChat,
  useConversations,
  ConversationList,
  ChatMessageList,
  ChatInput,
  ChatTopBar,
  UsageWarningBanner,
  ProfileIncompleteWarningBanner,
  ApprovalCard,
  ArtifactSidebar,
} from '@/features/chat'
import { useNotificationsContext } from '@/context/NotificationsContext'
import { NotificationBell } from '@/features/hubspot/components/NotificationBell'
import { NotificationTray } from '@/features/hubspot/components/NotificationTray'
import type { NotificationRecord, LeadData } from '@/features/hubspot/services/notificationsApi'
import * as chatApi from '@/features/chat/services/chatApi'

// Mirrors features/dashboard/hooks/useStartZephChat.ts's ZephChatSeed — the seed is
// passed untyped through router state, so this is the receiving contract.
type ZephChatSeed = {
  seedKey: string
  title: string
  briefing: string
  hubspotUrl?: string | null
}

const ChatPage: React.FC = () => {
  const { currentTheme, userData } = useTheme()
  const location = useLocation()
  const navigate = useNavigate()
  const [convListOpen, setConvListOpen] = useState(false)
  const [activeArtifact, setActiveArtifact] = useState<{ id: string; title: string; content: string } | null>(null)
  // lead context per conversation (for lead-contextualized chats)
  const [leadContextMap, setLeadContextMap] = useState<Record<string, LeadData>>({})
  // "Open in HubSpot" URL per conversation, for record-seeded chats (dashboard tasks, etc.)
  const [hubspotChatUrls, setHubspotChatUrls] = useState<Record<string, string>>({})
  const [seedError, setSeedError] = useState<string | null>(null)
  // A seed arriving via router state is held here and only acted on once the
  // conversation layer is ready (workspace resolved, list loaded) — see the effects
  // below. Fixes the mount race where createConversation() threw "No workspace
  // available" and the failure was swallowed. State (not a ref) so setting it
  // re-runs the runner effect even when ChatPage was already mounted.
  const [pendingSeed, setPendingSeed] = useState<ZephChatSeed | null>(null)
  const seedRunningRef = useRef(false)
  // A plain prompt handed over through router state (e.g. "Create with Zeph" on the
  // Campaigns tab): sent as a visible first message in a brand-new conversation.
  // Same hold-until-ready handling as the seed above.
  const [pendingStartPrompt, setPendingStartPrompt] = useState<string | null>(null)
  const startPromptRunningRef = useRef(false)
  const {
    conversations,
    activeConversationId,
    setActiveConversationId,
    createConversation,
    updateConversation,
    deleteConversation,
    workspaceId,
    loading: convsLoading,
  } = useConversations()

  const {
    allMessages,
    typingConvId,
    loadingConvId,
    currentStatus,
    pendingSteps,
    usageWarning,
    dismissUsageWarning,
    profileIncompleteWarning,
    dismissProfileIncompleteWarning,
    refreshUsageStatus,
    pendingApproval,
    sendMessage,
    loadMessages,
    stopStream,
    clearMessages,
    handleApprovalDecision,
    retry,
    retryableConvs,
  } = useChat()

  const { notifications, dismiss: dismissNotification } = useNotificationsContext()

  const messages = activeConversationId ? (allMessages[activeConversationId] ?? []) : []
  const isTyping = activeConversationId !== null && typingConvId === activeConversationId
  const isLoadingMessages = activeConversationId !== null && loadingConvId === activeConversationId
  const isStreaming = messages.some(m => m.isStreaming)
  const isPendingApproval = activeConversationId !== null && pendingApproval?.conversationId === activeConversationId
  const inputDisabled = isTyping || isStreaming || isLoadingMessages || convsLoading || isPendingApproval

  const userName = userData?.first_name?.trim() || undefined
  const userRole = userData?.role || 'member'

  // Load messages when switching conversations (only if not already loaded)
  useEffect(() => {
    if (activeConversationId && !allMessages[activeConversationId]) {
      loadMessages(activeConversationId)
    }
  }, [activeConversationId, allMessages, loadMessages])

  // The usage banner is otherwise only updated via SSE events at the end of a
  // completed turn, so it goes stale the moment an owner changes an
  // allocation mid-session. Re-check on load/conversation switch and whenever
  // the tab regains focus (e.g. coming back from the Settings page).
  useEffect(() => {
    if (!workspaceId) return
    refreshUsageStatus(workspaceId)

    const onFocus = () => refreshUsageStatus(workspaceId)
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onFocus)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onFocus)
    }
  }, [workspaceId, activeConversationId, refreshUsageStatus])

  const handleSend = useCallback(
    async (content: string) => {
      let convId = activeConversationId

      if (!convId) {
        try {
          convId = await createConversation()
        } catch {
          return
        }
      }

      // Optimistic placeholder — the AI-generated title (via onTitleGenerated below)
      // overwrites this once ready, so it should only ever change once, cleanly.
      const userMessages = (allMessages[convId] ?? []).filter(m => m.role === 'user')
      const isFirstMessage = userMessages.length === 0
      if (isFirstMessage) {
        const title = content.length > 42 ? `${content.slice(0, 39)}...` : content
        updateConversation(convId, { title, lastMessage: content, timestamp: new Date() })
      } else {
        updateConversation(convId, { lastMessage: content, timestamp: new Date() })
      }

      try {
        await sendMessage(convId, content, {
          onTitleGenerated: (title) => updateConversation(convId!, { title }),
        })
      } catch {
        return
      }
    },
    [activeConversationId, createConversation, sendMessage, allMessages, updateConversation]
  )

  const handleStop = useCallback(() => {
    if (activeConversationId) stopStream(activeConversationId)
  }, [activeConversationId, stopStream])

  const handleRetry = useCallback(() => {
    if (activeConversationId) retry(activeConversationId)
  }, [activeConversationId, retry])

  const handleClear = useCallback(() => {
    if (activeConversationId) clearMessages(activeConversationId)
  }, [activeConversationId, clearMessages])

  const handleNewChat = useCallback(() => {
    setActiveConversationId(null)
  }, [setActiveConversationId])

  const handleDelete = useCallback(
    async (id: string) => {
      await deleteConversation(id)
    },
    [deleteConversation]
  )

  const handleTriggerAction = useCallback((type: string, payload: any) => {
    let text = ''
    if (type === 'gmail_send_email' || type === 'outlook_send_email') {
      text = `Send email to ${payload.to} with subject "${payload.subject}" and body:\n${payload.body}`
    } else if (type === 'hubspot_create_contact') {
      text = `Create a contact in HubSpot with email ${payload.email}, name ${payload.first_name} ${payload.last_name}`
    } else if (type === 'hubspot_update_contact') {
      text = `Update the HubSpot contact ${payload.id} with email ${payload.email} and name ${payload.first_name}`
    } else if (type === 'hubspot_log_note') {
      text = `Log a CRM note against record ${payload.targetId}: ${payload.body}`
    } else {
      text = `Run action ${type} with payload: ${JSON.stringify(payload)}`
    }
    handleSend(text)
  }, [handleSend])

  /**
   * Called when the user clicks "Start an Action" on a lead notification.
   * 1. Creates a new conversation.
   * 2. Injects a hidden system context message so the AI knows which lead.
   * 3. Stores the lead context for the LeadActionWelcome screen.
   * 4. Dismisses the notification.
   */
  const handleStartAction = useCallback(async (notification: NotificationRecord) => {
    const lead = notification.lead
    if (!lead) return

    try {
      // Navigate to the chat page first (no-op if already there)
      let convId: string
      try {
        convId = await createConversation()
      } catch {
        return
      }

      // Store lead context so LeadActionWelcome renders for this conversation
      setLeadContextMap((prev) => ({ ...prev, [convId]: lead }))

      // Inject a system-level message with lead context as the first user message
      // (hidden from the welcome view, shown once the user picks an action)
      const leadName = lead.name || lead.email || `Lead ${lead.id}`
      const contextPrompt = [
        `[Lead Context — do not ask the user to identify the lead]`,
        `You are assisting with a HubSpot lead that has just been assigned.`,
        `Lead ID: ${lead.id}`,
        lead.name ? `Name: ${lead.name}` : null,
        lead.email ? `Email: ${lead.email}` : null,
        lead.company ? `Company: ${lead.company}` : null,
        lead.phone ? `Phone: ${lead.phone}` : null,
        `HubSpot Object Type: ${lead.hubspotObjectType}`,
        ``,
        `When the user asks you to perform any action, always operate on this lead (${leadName}) unless they explicitly specify a different contact. Do not ask "which lead?" — you already know.`,
      ].filter(Boolean).join('\n')

      // Post the context as a user message, then immediately stream to seed the AI memory
      await chatApi.createMessage(convId, contextPrompt)

      // Update conversation title optimistically
      updateConversation(convId, {
        title: `Lead: ${leadName}`,
        timestamp: new Date(),
      })

      // Dismiss the notification
      dismissNotification(notification.id)
    } catch {
      // Silent fail — user can still interact manually
    }
  }, [createConversation, updateConversation, dismissNotification])

  /**
   * Called when a user clicks an action chip in the Pending Actions section.
   * Creates a lead-context conversation and immediately sends the action prompt.
   */
  const handleLeadAction = useCallback(async (notification: NotificationRecord, promptText: string) => {
    const lead = notification.lead
    if (!lead) return
    try {
      const convId = await createConversation()
      setLeadContextMap((prev) => ({ ...prev, [convId]: lead }))
      const leadName = lead.name || lead.email || `Lead ${lead.id}`
      const contextPrompt = [
        `[Lead Context — do not ask the user to identify the lead]`,
        `You are assisting with a HubSpot lead that has just been assigned.`,
        `Lead ID: ${lead.id}`,
        lead.name ? `Name: ${lead.name}` : null,
        lead.email ? `Email: ${lead.email}` : null,
        lead.company ? `Company: ${lead.company}` : null,
        lead.phone ? `Phone: ${lead.phone}` : null,
        `HubSpot Object Type: ${lead.hubspotObjectType}`,
        ``,
        `When the user asks you to perform any action, always operate on this lead (${leadName}) unless they explicitly specify a different contact. Do not ask "which lead?" — you already know.`,
      ].filter(Boolean).join('\n')
      await chatApi.createMessage(convId, contextPrompt)
      updateConversation(convId, { title: `Lead: ${leadName}`, timestamp: new Date() })
      dismissNotification(notification.id)
      await sendMessage(convId, promptText, {
        onTitleGenerated: (title) => updateConversation(convId, { title }),
      })
    } catch {
      // Silent fail
    }
  }, [createConversation, updateConversation, dismissNotification, sendMessage])

  /**
   * Starts (or reopens) a Zeph conversation seeded from a dashboard card. Keyed by
   * `seed.seedKey` so re-clicking the same task/ticket reopens its existing chat
   * instead of making a new one. A fresh conversation gets a hidden briefing → Zeph
   * opens with the details + a recommendation. `hubspotUrl` drives the top-bar
   * "Open in HubSpot" button.
   */
  const runSeededConversation = useCallback(
    async (seed: ZephChatSeed) => {
      setSeedError(null)
      try {
        // Get-or-create: the backend returns the caller's existing conversation for
        // this seedKey when one exists (useConversations also sets it active).
        const convId = await createConversation({ title: seed.title, externalThreadId: seed.seedKey })
        if (seed.hubspotUrl) {
          setHubspotChatUrls((prev) => ({ ...prev, [convId]: seed.hubspotUrl! }))
        }

        // Seed only when Zeph hasn't opened this conversation yet — covers a fresh
        // conversation and one whose earlier seed attempt failed before Zeph replied.
        const existing = await chatApi.listMessages(convId)
        const zephHasReplied = existing.some((m) => m.role === 'assistant')
        if (!zephHasReplied) {
          updateConversation(convId, { title: seed.title, timestamp: new Date() })
          await sendMessage(convId, seed.briefing, {
            hidden: true,
            onTitleGenerated: () => updateConversation(convId, { title: seed.title }),
          })
        }
      } catch {
        setSeedError('Couldn’t open that chat. Please try again from the dashboard.')
      }
    },
    [createConversation, updateConversation, sendMessage]
  )

  // 1. Capture a seed arriving via router state, then clear history state so a
  //    refresh can't replay it. The work itself waits for effect (2).
  useEffect(() => {
    const state = location.state as {
      leadNotification?: NotificationRecord
      leadPromptText?: string
      zephSeed?: ZephChatSeed
      startPrompt?: string
    } | null
    if (!state) return

    navigate(location.pathname, { replace: true, state: null })

    if (state.zephSeed) {
      setPendingSeed(state.zephSeed)
      return
    }

    if (state.startPrompt) {
      setPendingStartPrompt(state.startPrompt)
      return
    }

    if (!state.leadNotification) return
    if (state.leadPromptText) {
      handleLeadAction(state.leadNotification, state.leadPromptText)
    } else {
      handleStartAction(state.leadNotification)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  // 2. Run the pending seed once the conversation layer is ready — workspace
  //    resolved and the list loaded. Runs exactly once per seed.
  useEffect(() => {
    if (!pendingSeed || !workspaceId || convsLoading || seedRunningRef.current) return
    seedRunningRef.current = true
    const seed = pendingSeed
    setPendingSeed(null)
    void runSeededConversation(seed).finally(() => {
      seedRunningRef.current = false
    })
  }, [pendingSeed, workspaceId, convsLoading, runSeededConversation])

  // Always a new conversation (never the one currently open) — each "Create with
  // Zeph" click is its own chat.
  const startConversationWithPrompt = useCallback(
    async (prompt: string) => {
      setSeedError(null)
      try {
        const convId = await createConversation()
        const title = prompt.length > 42 ? `${prompt.slice(0, 39)}...` : prompt
        updateConversation(convId, { title, lastMessage: prompt, timestamp: new Date() })
        await sendMessage(convId, prompt, {
          onTitleGenerated: (generated) => updateConversation(convId, { title: generated }),
        })
      } catch {
        setSeedError('Couldn’t start that chat. Please try again.')
      }
    },
    [createConversation, updateConversation, sendMessage]
  )

  useEffect(() => {
    if (!pendingStartPrompt || !workspaceId || convsLoading || startPromptRunningRef.current) return
    startPromptRunningRef.current = true
    const prompt = pendingStartPrompt
    setPendingStartPrompt(null)
    void startConversationWithPrompt(prompt).finally(() => {
      startPromptRunningRef.current = false
    })
  }, [pendingStartPrompt, workspaceId, convsLoading, startConversationWithPrompt])

  const activeConversation = conversations.find(c => c.id === activeConversationId)

  return (
    <div
      className="flex flex-col h-full overflow-hidden"
      style={{ backgroundColor: currentTheme.primary }}
    >
      {/* Portals to document.body — mounted here (not near the bell) purely
          so a fresh notification is impossible to miss while using the app,
          regardless of which conversation or panel is open. This existed as
          a component already but was never actually rendered anywhere. */}
      <NotificationTray
        notifications={notifications}
        onDismiss={dismissNotification}
        onStartAction={handleStartAction}
      />

      <div className="flex flex-1 min-h-0 overflow-hidden pb-16 md:pb-0">
      <Sidebar />

        <div className="flex flex-1 min-h-0 overflow-hidden">
          <ConversationList
            conversations={conversations}
            activeId={activeConversationId}
            onSelect={setActiveConversationId}
            onCreate={handleNewChat}
            onDelete={handleDelete}
            loading={convsLoading}
            mobileOpen={convListOpen}
            onCloseMobile={() => setConvListOpen(false)}
          />

          <div className="flex flex-1 overflow-hidden min-w-0">
            <div className="flex flex-col flex-1 min-h-0 overflow-hidden min-w-0">
              <ChatTopBar
                title={activeConversation?.title ?? 'New conversation'}
                onClear={handleClear}
                onOpenConversations={() => setConvListOpen(true)}
                hubspotUrl={activeConversationId ? hubspotChatUrls[activeConversationId] ?? null : null}
                notificationBell={
                  <NotificationBell
                    notifications={notifications}
                    onDismiss={dismissNotification}
                    onStartAction={handleStartAction}
                    onLeadAction={handleLeadAction}
                  />
                }
              />

              <ChatMessageList
                messages={messages}
                isTyping={isTyping}
                isLoadingMessages={isLoadingMessages}
                userName={userName}
                onSuggestionClick={handleSend}
                currentStatus={currentStatus}
                pendingSteps={pendingSteps}
                onOpenArtifact={(id, title, content) => setActiveArtifact({ id, title, content })}
                onTriggerAction={handleTriggerAction}
                isRetryable={activeConversationId ? retryableConvs[activeConversationId] : false}
                onRetry={handleRetry}
                leadContext={activeConversationId ? leadContextMap[activeConversationId] ?? null : null}
                onLeadActionSelect={handleSend}
              />

              {seedError && (
                <div
                  className="flex items-center justify-between gap-3 px-4 py-2 text-xs border-t"
                  style={{ backgroundColor: currentTheme.errorBackground, color: currentTheme.errorText, borderColor: currentTheme.border }}
                >
                  <span>{seedError}</span>
                  <button
                    type="button"
                    onClick={() => setSeedError(null)}
                    className="font-medium underline underline-offset-2 shrink-0"
                  >
                    Dismiss
                  </button>
                </div>
              )}

              {usageWarning && (
                <UsageWarningBanner
                  percentUsed={usageWarning.percentUsed}
                  remaining={usageWarning.remaining}
                  onDismiss={dismissUsageWarning}
                />
              )}

              {profileIncompleteWarning && (
                <ProfileIncompleteWarningBanner onDismiss={dismissProfileIncompleteWarning} />
              )}

              {isPendingApproval && pendingApproval ? (
                <ApprovalCard
                  pendingApproval={pendingApproval}
                  onDecision={(decision) => handleApprovalDecision(activeConversationId!, decision)}
                />
              ) : (
                <ChatInput
                  onSend={handleSend}
                  disabled={inputDisabled}
                  isStreaming={isTyping || isStreaming}
                  onStop={handleStop}
                  workspaceId={workspaceId}
                  showChips={messages.length === 0}
                  userRole={userRole}
                  onClearConversation={handleClear}
                  onNewConversation={handleNewChat}
                />
              )}
            </div>

            <ArtifactSidebar
              isOpen={activeArtifact !== null}
              onClose={() => setActiveArtifact(null)}
              title={activeArtifact?.title ?? ''}
              htmlContent={activeArtifact?.content ?? ''}
              onRewriteSelection={async (selectedText, instruction) => {
                handleSend(`Rewrite selection "${selectedText}": ${instruction}`)
              }}
            />
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  )
}

export default ChatPage
